import { Spacer, Stack, HStack, Heading, useDisclosure } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from 'antd';

import CategorySelectionModal from '@components/general/modals/category-selection-modal.component';
import { useUserStore } from '@store/app.store';
import { useSetDocumentTitle, useStyledToast } from '@util/hooks';
import { tableParamsToBody } from '@util/helpers/table.helpers.jsx';
import {
  filtersToRecordingsSearchBody,
  useRecordingsColumns
} from '@util/helpers/recordings.helpers';
import { useSearchRecordings } from '@services/query-services/recordings.query-service';
import { useCreateMeeting } from '@services/query-services/meetings.query-service';
import {
  usePostZoomDownloadMeetingRecording,
  useLinkRecordingToMeeting
} from '@services/query-services/lambda.query-service';

export default function RecordingsAdminPage() {
  useSetDocumentTitle('Recordings Admin - FORA');
  const toast = useStyledToast();
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const tenant = useUserStore((state) => state.tenant);
  const getSessionId = useUserStore((state) => state?.getSessionId);
  const [total, setTotal] = useState(0);
  const [recordingsArr, setRecordingsArr] = useState([]);
  const [uploadingRecords, setUploadingRecords] = useState(new Set());
  const [selectedRecord, setSelectedRecord] = useState(null);
  const categoryModal = useDisclosure();

  const { mutateAsync: createMeeting } = useCreateMeeting();
  const { mutateAsync: downloadMeetingRecording } = usePostZoomDownloadMeetingRecording();
  const { mutateAsync: linkRecordingToMeeting } = useLinkRecordingToMeeting();

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 20
    }
  });

  const {
    data: recordingsRes,
    isLoading,
    refetch
  } = useSearchRecordings(tableParamsToBody(tableParams, filtersToRecordingsSearchBody), {
    includeTotal: true
  });

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({ pagination, filters, sorter });
  };

  const handleUploadRecording = (record) => {
    setSelectedRecord(record);
    categoryModal.onOpen();
  };

  const handleViewSummary = (record) => {
    if (record.meeting?.id) {
      navigate(`/meeting/${record.meeting.id}`);
    }
  };

  const handleCategoryConfirm = async (selectedCategory) => {
    if (selectedRecord && selectedCategory) {
      await performUpload(selectedRecord, selectedCategory);
      setSelectedRecord(null);
    }
  };

  const performUpload = async (record, selectedCategory) => {
    try {
      setUploadingRecords((prev) => new Set(prev).add(record.id));

      // Create meeting first
      const meetingData = {
        title: record.title,
        description: record.title,
        url: record.url || '',
        tenant: { id: tenant.id },
        initiative_match: 'Auto',
        initiative: { id: selectedCategory.value },
        start_at: record.start_at,
        end_at: record.end_at,
        creator: { id: user.id },
        tags: ['meeting_upload']
      };

      const createdMeeting = await createMeeting(meetingData);

      // Link the recording to the created meeting via lambda function
      await linkRecordingToMeeting({
        recordingId: record.id,
        meetingId: createdMeeting.id
      });

      // Determine subtype based on available data
      const downloadParams = {
        meeting_id: record.external_id,
        person: user,
        fora_meeting: createdMeeting,
        session_id: getSessionId(),
        video_cloud_key: record.video_cloud_key,
        transcript_cloud_key: record.transcript_cloud_key
      };

      // Add subtype based on available data
      if (!record.transcript_cloud_key && record.video_cloud_key) {
        downloadParams.subtype = 'convert-from-video';
      } else if (!record.transcript_cloud_key && !record.video_cloud_key) {
        throw new Error('No transcript or video data available');
      }

      await downloadMeetingRecording(downloadParams);

      toast({
        title: 'Upload initiated',
        description: 'The recording is being processed by FORA',
        status: 'success',
        duration: 9000,
        isClosable: true
      });

      // Refresh the table data
      refetch();
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: 'Failed to upload recording. Please try again.',
        status: 'error',
        duration: 9000,
        isClosable: true
      });
    } finally {
      setUploadingRecords((prev) => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
    }
  };

  // const handleBulkUpload = () => {
  //   // TODO: Implement bulk upload functionality
  //   console.log('Bulk upload recordings');
  // };

  const columns = useRecordingsColumns({
    onUpload: handleUploadRecording,
    onViewSummary: handleViewSummary,
    uploadingRecords
  });

  useEffect(() => {
    refetch();
  }, [tableParams]);

  useEffect(() => {
    setRecordingsArr(recordingsRes?.data || []);
    setTotal(recordingsRes?.total || 0);
  }, [recordingsRes]);

  return (
    <Stack
      p={11}
      gap={11}
    >
      <HStack gap={4}>
        <Heading
          size={{ base: 'sm', lg: '2xl' }}
          lineHeight={1.3}
        >
          Recordings
        </Heading>
        <Spacer />
        {/* TODO */}
        {/*        <Button
          onClick={handleBulkUpload}
          size='reskinMd'
          variant='reskinPrimary'
        >
          Upload to FORA (bulk)
        </Button>*/}
      </HStack>

      <Table
        className='recordings-admin-table'
        size='small'
        rowKey='id'
        loading={isLoading}
        columns={columns}
        dataSource={recordingsArr}
        pagination={{ ...tableParams.pagination, total }}
        onChange={handleTableChange}
      />

      <CategorySelectionModal
        disclosure={categoryModal}
        onConfirm={handleCategoryConfirm}
        isLoading={selectedRecord ? uploadingRecords.has(selectedRecord.id) : false}
      />
    </Stack>
  );
}
